import { useEffect, useState, useCallback, useMemo } from "react";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Plus, Trash, MagnifyingGlass, CaretDown, CaretRight } from "@phosphor-icons/react";
import { DisclaimerBanner } from "../components/Disclaimer";

export default function PriceList() {
  const [data, setData] = useState({ sector: "", default: [], custom: [] });
  const [show, setShow] = useState(false);
  const [search, setSearch] = useState("");
  const [openCat, setOpenCat] = useState({});
  const [form, setForm] = useState({ code: "", description: "", unit: "mq", price: 0, keywords: "" });

  const load = useCallback(async () => {
    const { data } = await api.get("/pricelist");
    setData(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/pricelist", { ...form, keywords: form.keywords.split(",").map((s)=>s.trim()).filter(Boolean), price: parseFloat(form.price) });
      toast.success("Aggiunto"); setShow(false);
      setForm({ code: "", description: "", unit: "mq", price: 0, keywords: "" });
      load();
    } catch { toast.error("Errore"); }
  };

  const remove = async (id) => { await api.delete(`/pricelist/${id}`); load(); };

  const grouped = useMemo(() => {
    const q = search.toLowerCase().trim();
    const filter = (it) => {
      if (!q) return true;
      return (it.code || "").toLowerCase().includes(q)
        || (it.description || "").toLowerCase().includes(q)
        || (it.keywords || []).some((k) => k.toLowerCase().includes(q));
    };
    const map = {};
    data.default.filter(filter).forEach((it) => {
      const cat = it.category || "Altro";
      (map[cat] = map[cat] || []).push(it);
    });
    return Object.entries(map).sort(([a], [b]) => a.localeCompare(b));
  }, [data.default, search]);

  const totalShown = grouped.reduce((s, [, items]) => s + items.length, 0);

  const toggleCat = (cat) => setOpenCat((s) => ({ ...s, [cat]: !s[cat] }));
  const openAll = () => setOpenCat(Object.fromEntries(grouped.map(([c]) => [c, true])));
  const closeAll = () => setOpenCat({});

  return (
    <Layout>
      <div className="flex justify-between items-start mb-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#A3A3A3] font-bold">Settore: {data.sector}</div>
          <h1 className="font-heading text-4xl font-black uppercase tracking-tighter leading-none mt-1">Listino DEI</h1>
          <div className="text-xs text-[#A3A3A3] mt-1 font-mono">{data.default.length} voci · {data.custom?.length || 0} personalizzate</div>
        </div>
        <button data-testid="add-price-btn" onClick={()=>setShow(true)}
          className="bg-[#FFD600] text-[#0A0A0A] h-12 px-4 rounded-md font-black uppercase flex items-center gap-2">
          <Plus size={20} weight="bold"/>Custom
        </button>
      </div>

      <DisclaimerBanner />

      <div className="relative mb-3">
        <MagnifyingGlass size={20} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A3A3A3]"/>
        <input data-testid="pl-search" value={search} onChange={(e)=>setSearch(e.target.value)} placeholder="Cerca voce, codice o keyword…"
          className="w-full bg-[#1A1A1A] border-2 border-[#333] h-12 rounded-md pl-11 pr-3 text-base focus:border-[#FFD600] focus:outline-none"/>
      </div>

      {data.custom?.length > 0 && (
        <div className="mb-4">
          <div className="text-xs uppercase tracking-wider text-[#FFD600] font-bold mb-2">Voci personalizzate</div>
          <div className="space-y-2">
            {data.custom.map((p) => (
              <div key={p.id} className="bg-[#1A1A1A] border border-[#FFD600]/30 rounded-md p-3 flex justify-between gap-2" data-testid={`custom-${p.code}`}>
                <div className="flex-1">
                  <div className="font-mono text-xs text-[#FFD600]">{p.code}</div>
                  <div className="text-sm font-bold">{p.description}</div>
                  <div className="text-xs text-[#A3A3A3]">€ {p.price} / {p.unit}</div>
                </div>
                <button onClick={()=>remove(p.id)} className="text-[#FF3B30] p-1"><Trash size={20} weight="bold"/></button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex gap-2 mb-3 text-xs">
        <button onClick={openAll} className="text-[#FFD600] font-bold uppercase" data-testid="open-all">Apri tutto</button>
        <span className="text-[#333]">·</span>
        <button onClick={closeAll} className="text-[#A3A3A3] font-bold uppercase" data-testid="close-all">Chiudi</button>
        {search && <span className="ml-auto text-[#A3A3A3] font-mono">{totalShown} risultati</span>}
      </div>

      <div className="space-y-2">
        {grouped.map(([cat, items]) => {
          const isOpen = openCat[cat] || !!search;
          return (
            <div key={cat} className="bg-[#1A1A1A] border border-[#333] rounded-md overflow-hidden">
              <button data-testid={`cat-${cat}`} onClick={()=>toggleCat(cat)}
                className="w-full px-4 py-3 flex items-center justify-between hover:bg-[#0A0A0A] transition">
                <div className="flex items-center gap-2">
                  {isOpen ? <CaretDown size={16} weight="bold" className="text-[#FFD600]"/> : <CaretRight size={16} weight="bold" className="text-[#A3A3A3]"/>}
                  <span className="font-heading text-lg font-black uppercase tracking-tight">{cat}</span>
                </div>
                <span className="font-mono text-xs text-[#A3A3A3]">{items.length}</span>
              </button>
              {isOpen && (
                <div className="border-t border-[#333] divide-y divide-[#333]">
                  {items.map((p) => (
                    <div key={p.code} className="px-4 py-2.5 hover:bg-[#0A0A0A]" data-testid={`default-${p.code}`}>
                      <div className="flex justify-between items-start gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="font-mono text-[10px] text-[#FFD600] tracking-wide">{p.code}</div>
                          <div className="text-sm font-bold leading-snug">{p.description}</div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="font-mono text-sm font-bold text-[#FFD600]">€ {p.price.toFixed(2)}</div>
                          <div className="text-[10px] uppercase text-[#A3A3A3] tracking-wider">/ {p.unit}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
        {grouped.length === 0 && (
          <div className="bg-[#1A1A1A] border border-[#333] rounded-md p-8 text-center text-[#A3A3A3] text-sm">
            Nessun risultato per "{search}"
          </div>
        )}
      </div>

      {show && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-end sm:items-center justify-center p-2" onClick={()=>setShow(false)}>
          <form onSubmit={submit} className="bg-[#1A1A1A] border border-[#333] rounded-md w-full max-w-lg" onClick={(e)=>e.stopPropagation()}>
            <div className="border-b border-[#333] p-4 font-heading text-xl font-bold uppercase">Voce personalizzata</div>
            <div className="p-4 space-y-3">
              <input data-testid="pl-code" required placeholder="Codice (es. CUST.01)" value={form.code} onChange={(e)=>setForm({...form, code: e.target.value})}
                className="w-full bg-[#0A0A0A] border-2 border-[#333] h-12 rounded-md px-3 focus:border-[#FFD600] focus:outline-none"/>
              <input data-testid="pl-desc" required placeholder="Descrizione" value={form.description} onChange={(e)=>setForm({...form, description: e.target.value})}
                className="w-full bg-[#0A0A0A] border-2 border-[#333] h-12 rounded-md px-3 focus:border-[#FFD600] focus:outline-none"/>
              <div className="grid grid-cols-2 gap-2">
                <select data-testid="pl-unit" value={form.unit} onChange={(e)=>setForm({...form, unit: e.target.value})}
                  className="bg-[#0A0A0A] border-2 border-[#333] h-12 rounded-md px-3 focus:border-[#FFD600] focus:outline-none">
                  {["mq","ml","cad","mc","kg","h","g","mese","ha","kW"].map((u)=><option key={u} value={u}>{u}</option>)}
                </select>
                <input data-testid="pl-price" required type="number" step="0.01" placeholder="Prezzo €" value={form.price} onChange={(e)=>setForm({...form, price: e.target.value})}
                  className="bg-[#0A0A0A] border-2 border-[#333] h-12 rounded-md px-3 focus:border-[#FFD600] focus:outline-none"/>
              </div>
              <input data-testid="pl-kw" placeholder="Parole chiave (separate da virgola)" value={form.keywords} onChange={(e)=>setForm({...form, keywords: e.target.value})}
                className="w-full bg-[#0A0A0A] border-2 border-[#333] h-12 rounded-md px-3 focus:border-[#FFD600] focus:outline-none"/>
              <div className="flex gap-2">
                <button type="button" onClick={()=>setShow(false)} className="flex-1 bg-[#1A1A1A] border-2 border-[#333] h-12 rounded-md font-black uppercase">Annulla</button>
                <button data-testid="pl-save" type="submit" className="flex-1 bg-[#FFD600] text-[#0A0A0A] h-12 rounded-md font-black uppercase">Salva</button>
              </div>
            </div>
          </form>
        </div>
      )}
    </Layout>
  );
}
